<template>
  <div id="repayment-simulation">
    <HeadingOne>ご返済シミュレーション</HeadingOne>

    <p class="_mb-md-60 _mb-sm-40">
      お借り入れ希望額とお借り入れ利率から、月々のご返済金額とご返済期間を算出します。
    </p>

    <form novalidate @submit.prevent="doSimulation">
      <TableBox class="_mb-md-30 _mb-sm-20">
        <TableBody>
          <TableRow>
            <TableHeader class="-entry">
              お借り入れご希望額<small class="moji">【半角数字】</small>
            </TableHeader>
            <TableData :has-error="errors['desiredAmount']">
              <InputGroup :errors="errors" :prop-names="['desiredAmount']">
                <InputgroupAppend>
                  <InputItem
                    id="desiredAmount"
                    v-model="desiredAmount"
                    type="text"
                    empty-color="true"
                    :maxlength="3"
                    class="-xs"
                    @change="resetSimulationResults"
                  ></InputItem>
                  <InputAppend
                    >万円（1万～100万円までの入力ができます。）</InputAppend
                  >
                </InputgroupAppend>
                <InputLabel> 入力例：20万円の場合「20」 </InputLabel>
              </InputGroup>
            </TableData>
          </TableRow>
          <TableRow>
            <TableHeader class="-entry"
              >お借り入れ利率 <small>（実質年率）</small
              ><small class="moji">【半角数字、小数点第2位】</small>
            </TableHeader>
            <TableData :has-error="errors['interestRate']">
              <InputGroup :errors="errors" :prop-names="['interestRate']">
                <InputgroupAppend>
                  <InputItem
                    v-model="interestRate"
                    type="text"
                    empty-color="true"
                    :maxlength="5"
                    class="-xs"
                    step="0.01"
                    @change="resetSimulationResults"
                  ></InputItem>
                  <InputAppend
                    >％（2.90～18.00％までの入力ができます。）</InputAppend
                  >
                </InputgroupAppend>
                <InputLabel> 入力例：18.00 </InputLabel>
                <ListGroup class="-note">
                  <ListItem class="-note"
                    >お借り入れご希望金額が100万円の場合、2.90～15.00％のシミュレーションが可能です。</ListItem
                  >
                </ListGroup>
              </InputGroup>
            </TableData>
          </TableRow>
        </TableBody>
      </TableBox>

      <CardBox class="_mb-md-30 _mb-sm-20">
        <CardHeader>
          <h2 class="heading">ご返済シミュレーションの注意事項</h2>
        </CardHeader>
        <CardBody>
          <ListGroup class="-note">
            <ListItem class="-note"
              >本シミュレーションの結果は参考値となり、実際のご返済金額・期間とは異なる場合がございます。</ListItem
            >
            <ListItem class="-note"
              >お借り入れ利率（実質年率）は審査後にご提示させていただきます。</ListItem
            >
          </ListGroup>
        </CardBody>
      </CardBox>

      <BtngroupSingle class="-margin-lg">
        <BtnMain class="-normal" @click.prevent="doSimulation">
          計算する
        </BtnMain>
      </BtngroupSingle>
    </form>
    <div id="simulationResult"></div>
    <template v-if="showSimulationResult">
      <div class="triangle _mb-md-30 _mb-sm-20"></div>

      <ContentsCard class="-orange _mb-md-60 _mb-sm-30">
        <div class="card-header">
          <h3 class="heading -black">シミュレーション結果</h3>
        </div>
        <div class="card-body _pt-md-10">
          <div class="heading-sub _mb-md-30 _mb-sm-10">
            月々のご返済金額、ご返済期間を算出しました。
          </div>
          <div class="card-groups">
            <div class="card-box">
              <p class="txt01">
                <span class="txt02">月々のご返済金額</span
                >{{ numberToCurrency(getRepaymentAmount()) }}
              </p>
            </div>
            <div class="card-box _mb-sm-20">
              <p class="txt01">
                <span class="txt02">ご返済期間</span>{{ repaymentPeriod }}ヶ月
              </p>
            </div>
          </div>
        </div>
      </ContentsCard>

      <BtngroupSingle class="_mb-md-60 _mb-sm-30">
        <slot name="entryBtn"></slot>
      </BtngroupSingle>

      <h3 class="chart-title">ご返済表</h3>
      <ChartGroup class="toggle _mb-md-120 _mb-sm-60">
        <ChartBox id="simulation-results=table" gradient="true">
          <ChartHeader class="-pc-left -dark">
            <ChartRow>
              <ChartData rowspan="2" class="-sm">ご返済回数</ChartData>
              <ChartData rowspan="2" class="-blank -justify-4"
                >ご返済金額</ChartData
              >
              <ChartData class="-blank -justify-4"></ChartData>
              <ChartData class="-blank -justify-4"></ChartData>
              <ChartData rowspan="2" class="-justify-4"
                >ご返済後のご利用残高</ChartData
              >
            </ChartRow>
            <ChartRow>
              <ChartData class="-low">元本充当額</ChartData>
              <ChartData class="-low">利息充当額</ChartData>
            </ChartRow>
          </ChartHeader>
          <ChartBody class="-pc-right -sp-right">
            <ChartRow v-for="(item, index) in firstViewResults" :key="index">
              <ChartData class="heading -pc-left -sp-center">{{
                index + 1
              }}</ChartData>
              <ChartData>{{
                numberToCurrency(item.repaymentAmount)
              }}</ChartData>
              <ChartData>{{ numberToCurrency(item.principal) }}</ChartData>
              <ChartData>{{ numberToCurrency(item.interest) }}</ChartData>
              <ChartData>{{ numberToCurrency(item.balance) }}</ChartData>
            </ChartRow>
          </ChartBody>
        </ChartBox>
        <ChartBox
          id="more-simulation-results=table"
          class="-hide"
          toggle="true"
        >
          <ChartBody class="-pc-right -sp-right">
            <ChartRow v-for="(item, index) in moreViewResults" :key="index">
              <ChartData class="heading -pc-left -sp-center -sm">{{
                index + 4
              }}</ChartData>
              <ChartData class="-justify-4">{{
                numberToCurrency(item.repaymentAmount)
              }}</ChartData>
              <ChartData class="-justify-4">{{
                numberToCurrency(item.principal)
              }}</ChartData>
              <ChartData class="-justify-4">{{
                numberToCurrency(item.interest)
              }}</ChartData>
              <ChartData class="-justify-4">{{
                numberToCurrency(item.balance)
              }}</ChartData>
            </ChartRow>
          </ChartBody>
        </ChartBox>
      </ChartGroup>

      <BtngroupSingle class="_mb-md-60 _mb-sm-30">
        <slot name="nextBtn"></slot>
      </BtngroupSingle>
      <BtngroupSingle class="_mb-md-120 _mb-sm-60">
        <BtnMain class="-normal" @click.prevent="reSimulation">
          もう一度シミュレーションする
        </BtnMain>
      </BtngroupSingle>

      <slot name="cardContents"></slot>

      <ContactBox class="_mb-md-120 _mb-sm-60" :member="member" />
    </template>
  </div>
</template>

<script>
import HeadingOne from "@/components/heading/HeadingOne";
import TableBox from "@/components/tables/TableBox";
import TableRow from "@/components/tables/TableRow";
import TableHeader from "@/components/tables/TableHeader";
import TableData from "@/components/tables/TableData";
import InputGroup from "@/components/input/InputGroup";
import InputgroupAppend from "@/components/input/InputgroupAppend";
import InputItem from "@/components/input/InputItem";
import InputAppend from "@/components/input/InputAppend";
import InputLabel from "@/components/input/InputLabel";
import TableBody from "@/components/tables/TableBody";
import BtngroupSingle from "@/components/button/BtngroupSingle";
import BtnMain from "@/components/button/BtnMain";
import CardBox from "@/components/card/CardBox";
import CardHeader from "@/components/card/CardHeader";
import CardBody from "@/components/card/CardBody";
import ListGroup from "@/components/list/ListGroup";
import ListItem from "@/components/list/ListItem";
import ContentsCard from "@/components/contents/ContentsCard";
import ChartBox from "@/components/charts/ChartBox";
import ChartGroup from "@/components/charts/ChartGroup";
import ChartHeader from "@/components/charts/ChartHeader";
import ChartRow from "@/components/charts/ChartRow";
import ChartData from "@/components/charts/ChartData";
import ChartBody from "@/components/charts/ChartBody";
import ContactBox from "@/components/box/ContactBox";
import { numberToCurrency } from "@/utils/Filter";
import { scrollTop } from "@/utils/Other";

export default {
  name: "RepaymentSimulation",
  components: {
    ContactBox,
    ChartBody,
    ChartData,
    ChartRow,
    ChartHeader,
    ChartGroup,
    ChartBox,
    ContentsCard,
    ListItem,
    ListGroup,
    CardBody,
    CardHeader,
    CardBox,
    BtnMain,
    BtngroupSingle,
    TableBody,
    InputLabel,
    InputAppend,
    InputItem,
    InputgroupAppend,
    InputGroup,
    TableData,
    TableHeader,
    TableRow,
    TableBox,
    HeadingOne,
  },
  props: {
    showMyPageTopBtn: {
      type: Boolean,
      default: false,
    },
    showSingUpBtn: {
      type: Boolean,
      default: false,
    },
    member: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return {
      numberToCurrency,
    };
  },
  data() {
    return {
      errors: {},
      isDisabled: false,
      simulationResults: [],
      desiredAmount: "",
      interestRate: "",
    };
  },
  computed: {
    showSimulationResult() {
      // シミュレーション結果表示
      return this.simulationResults.length > 0;
    },
    firstViewResults() {
      return this.simulationResults.slice(0, 3);
    },
    moreViewResults() {
      return this.simulationResults.slice(3);
    },
    repaymentPeriod() {
      return this.simulationResults.length;
    },
    diffRepaymentPeriod() {
      return this.repaymentPeriod;
    },
  },
  methods: {
    validateDesiredAmount() {
      try {
        let reason = [];
        if (!this.desiredAmount) {
          reason.push("invalid-desiredamount");
        } else {
          if (isNaN(this.desiredAmount)) {
            reason.push("invalid-desiredamount");
          } else if (this.desiredAmount.length > 3) {
            reason.push("invalid-desiredamount");
          } else if (!this.desiredAmount.toString().trim()) {
            reason.push("invalid-desiredamount");
          } else if (this.desiredAmount.toString().indexOf(".") !== -1) {
            reason.push("invalid-desiredamount");
          } else {
            if (this.desiredAmount <= 0 || this.desiredAmount > 100) {
              reason.push("invalid-desiredamount");
            }
          }
        }
        if (reason.length > 0) {
          this.errors["desiredAmount"] = reason;
        }
      } catch (e) {
        this.goToErrorPage(e);
      }
    },
    validateInterestRate() {
      try {
        let reason = [];
        if (!this.interestRate) {
          reason.push("invalid-interestrate");
        } else {
          if (this.interestRate.replace(/[-¥.]/g, "").length > 4) {
            reason.push("invalid-interestrate");
          } else if (
            /^(([1][0-8]|[3-9])(\.[0-9]{1,2})?|2.9[0-9]?)$/.test(
              this.interestRate
            )
          ) {
            if (this.interestRate > 15 && Number(this.desiredAmount) === 100) {
              reason.push("invalid-desiredamount-interestrate");
            } else if (this.interestRate > 18) {
              reason.push("invalid-interestrate");
            }
          } else {
            if (Number(this.desiredAmount) === 100) {
              reason.push("invalid-desiredamount-interestrate");
            } else {
              reason.push("invalid-interestrate");
            }
          }
        }
        if (reason.length > 0) {
          this.errors["interestRate"] = reason;
        }
      } catch (e) {
        this.goToErrorPage(e);
      }
    },
    doSimulation() {
      if (this.isDisabled) {
        return;
      }
      this.isDisabled = true;
      this.errors = {};
      this.validateDesiredAmount();
      this.validateInterestRate();
      if (Object.keys(this.errors).length > 0) {
        scrollTop();
        this.isDisabled = false;
        return;
      }
      this.resetSimulationResults();
      this.simulationResults = this.calculation();
      let element = document.getElementById("simulationResult");
      this.$scrollTo(element, {
        duration: 300,
        easing: "ease-in",
        offset: -150,
      });
      this.isDisabled = false;
    },
    resetSimulationResults() {
      this.simulationResults = [];
    },
    reSimulation() {
      this.simulationResults = [];
      this.desiredAmount = "";
      this.interestRate = "";
      let element = document.getElementById("repayment-simulation");
      this.$scrollTo(element, {
        duration: 300,
        easing: "ease-in",
        offset: -150,
      });
    },
    calculation(advanceRepayment = 0) {
      let desiredAmount = Number(this.desiredAmount) * 10000;
      let interestRate = Number(this.interestRate);
      // 貸付日
      // let lendingDate = new Date(Date.now())
      let lendingDate = new Date(new Date().setDate(1));
      // 約定日
      let tradeDate = 26;
      // 前回返済期日
      let prevRepaymentDueDate = new Date(lendingDate.getTime());
      // 返済期日
      let repaymentDueDate = new Date(lendingDate.getTime());
      repaymentDueDate.setDate(tradeDate);
      if (lendingDate.getDay() > 15) {
        // 約定日、以降の場合は翌月
        repaymentDueDate.setMonth(repaymentDueDate.getMonth() + 1);
      }
      // 返済額
      let repaymentAmount =
        Number(this.getRepaymentAmount(desiredAmount)) +
        Number(advanceRepayment);
      // 残高
      let balance = desiredAmount;
      let results = [];
      let principal = 1;
      while (balance > 0 && principal > 0) {
        // 利息
        let interest = this.getInterest(
          balance,
          interestRate,
          prevRepaymentDueDate,
          repaymentDueDate
        );
        // 元金
        principal =
          repaymentAmount - interest > balance
            ? balance
            : repaymentAmount - interest;
        balance = balance - principal;
        // console.log(balance + ', ' + principal + ', ' + interest + ', ' + principal + interest)
        let scheduledItem = {
          balance: balance, // 残高
          principal: principal, // 元金
          interest: interest, // 利息
          repaymentAmount: principal + interest, // 返済金額
        };
        results.push(scheduledItem);
        prevRepaymentDueDate = new Date(repaymentDueDate.getTime());
        repaymentDueDate.setMonth(repaymentDueDate.getMonth() + 1);
      }
      return results;
    },
    getInterest(balance, interestRate, prevRepaymentDueDate, repaymentDueDate) {
      let termDay = Math.ceil(
        (repaymentDueDate - prevRepaymentDueDate) / 86400000
      );
      return Math.floor(balance * (interestRate / 100) * (termDay / 365));
    },
    getRepaymentAmount() {
      let desiredAmount = this.desiredAmount * 10000;
      if (desiredAmount >= 10000 && desiredAmount <= 100000) {
        return 4000;
      } else if (desiredAmount > 100000 && desiredAmount <= 200000) {
        return 8000;
      } else if (desiredAmount > 200000 && desiredAmount <= 300000) {
        return 11000;
      } else if (desiredAmount > 300000 && desiredAmount <= 400000) {
        return 11000;
      } else if (desiredAmount > 400000 && desiredAmount <= 500000) {
        return 13000;
      } else if (desiredAmount > 500000 && desiredAmount <= 600000) {
        return 16000;
      } else if (desiredAmount > 600000 && desiredAmount <= 700000) {
        return 18000;
      } else if (desiredAmount > 700000 && desiredAmount <= 800000) {
        return 21000;
      } else if (desiredAmount > 800000 && desiredAmount <= 900000) {
        return 23000;
      } else if (desiredAmount > 900000 && desiredAmount <= 1000000) {
        return 24000;
      }
      return 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/stylesheets/styles";

#repayment-simulation {
  :deep(.InputItem).-md {
    @include mq-up(md) {
      width: 300px;
    }
  }
  > .triangle {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 22px 60px 0 60px;
    border-color: $secondary transparent transparent transparent;
    margin: 0 auto;
  }
  > .chart-title {
    color: #333;
    text-align: left;
    font-size: 16px;
    line-height: 24px;
    padding-bottom: 10px;
    @include mq-up(md) {
      font-size: 24px;
      line-height: 32px;
    }
  }
  > .contents-card {
    @include mq-down(md) {
      padding-bottom: 0;
    }
    > .card-header {
      > .heading {
        color: $black;
        @include mq-up(md) {
          font-weight: normal;
        }
      }
    }
    > .card-body {
      background: none;
      border-radius: 0;
      padding: 0 10px;
      font-size: 14px;
      @include mq-up(md) {
        padding: 30px 0 10px;
        font-size: 16px;
      }
      > .heading-sub {
        @include mq-up(md) {
          text-align: center;
        }
        > .-underline {
          text-decoration: underline;
        }
        .txt03 {
          color: $orange;
          font-weight: bold;
          font-size: 20px;
          display: inline-block;
          margin: 0 5px;
          @include mq-up(md) {
            font-size: 24px;
            margin: 0 10px;
          }
        }
      }
      > .card-groups {
        display: flex;
        @include mq-down(md) {
          flex-wrap: wrap;
        }
        @include mq-up(md) {
          justify-content: space-between;
          align-items: center;
        }
        > .card-box {
          padding: 10px 0;
          background-color: $white;
          width: 100%;
          text-align: center;
          @include mq-up(md) {
            padding: 20px 0;
            margin: 0 10px;
            width: 50%;
          }
          @include mq-down(md) {
            margin: 10px 0;
          }
          > .txt01 {
            color: $orange;
            font-size: 20px;
            font-weight: bold;
            word-break: break-all;
            text-align: center;
            margin-bottom: 0;
            @include mq-down(md) {
              display: block;
              width: 100%;
              > .small {
                font-size: 14px;
              }
            }
            @include mq-up(md) {
              display: inline-block;
              font-size: 24px;
              > .small {
                font-size: 16px;
              }
            }
            > .txt02 {
              color: $black;
              padding-top: 5px;
              padding-bottom: 5px;
              font-size: 14px;
              font-weight: normal;
              display: block;
              @include mq-down(md) {
                width: 100%;
              }
              @include mq-up(md) {
                display: inline-block;
                margin-right: 20px;
                font-size: 16px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
